import React, { useEffect, useState } from 'react';
import { Title, Description } from '@radix-ui/react-dialog';

import { Dialog } from '$partials/ui';
import { PropertyAreaText, PropertyPriceText } from '$partials/properties';
import { PropertyListingFilters } from '$partials/filters/panels';
import { PropertyMap } from './property-map-view';
import { SimpleImage } from '$partials/media';
import IconChevron from '$partials/icons/icon-chevron';
import IconClose from '$partials/icons/icon-close';
import IconList from '$partials/icons/icon-list';
import IconMap from '$partials/icons/icon-map';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/listings.d.ts').PropertyMapModalHighlightProperty}
 */
const PropertyMapModalHighlightProperty = ({
    __typename,
    bathrooms,
    bedrooms,
    displayAddress,
    id,
    isOffMarket,
    listingImage,
    livingAreaMeters,
    nextPropertyId,
    onHighlightChange,
    previousPropertyId,
    price,
    priceCurrency,
    priceRentPeriod,
    url,
}) => {
    if (isOffMarket) { return null; }

    return (
        <article
            className='relative h-[100vw] max-h-[60svh]'
            key={id}>
            <div className='absolute top-0 right-0 p-16 pointer-events-none z-10'>
                <button
                    className='h-[32px] w-[32px] button-secondary-filled !p-0 pointer-events-auto'
                    onClick={() => onHighlightChange(null)}
                    type='button'>
                    <IconClose />
                </button>
            </div>

            <div className='expand-interaction flex flex-col p-8 pb-16 gap-12 h-full'>
                <div className='relative flex-1'>
                    <div className='absolute top-0 bottom-0 left-0 right-0 w-full overflow-hidden rounded'>
                        <SimpleImage
                            {...listingImage}
                            className='w-full h-full object-cover' />
                    </div>
                </div>

                {price &&
                    <div>
                        <div className='inline-flex items-center justify-center bg-tag-main-background border border-tag-main-border rounded h-24 p-8'>
                            <p className='type-style-eyebrow text-main-brand-text'>
                                <PropertyPriceText
                                    isOffMarket={isOffMarket}
                                    pageType={__typename}
                                    price={price}
                                    priceCurrency={priceCurrency}
                                    priceRentPeriod={priceRentPeriod} />
                            </p>
                        </div>
                    </div>
                }

                <div>
                    <a
                        className='expand-interaction__action type-style-eyebrow group-hover:text-main-brand-text'
                        href={url}>
                        <h3>{displayAddress}</h3>
                    </a>

                    <div className='flex gap-16 items-center type-style-eyebrow text-main-text-primary mt-4'>
                        {bedrooms && (
                            <p className='shrink-0'>
                                {bedrooms} bed
                            </p>
                        )}
                        {bathrooms && (
                            <p className='shrink-0'>
                                {bathrooms} bath
                            </p>
                        )}
                        {livingAreaMeters && (
                            <p className='shrink-0'>
                                <PropertyAreaText
                                    areaMeters={livingAreaMeters}
                                    isOffMarket={isOffMarket} />
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Zoom controls */}
            <div className='absolute bottom-0 right-0 text-center pointer-events-none z-10 p-16 flex gap-8'>
                <button
                    aria-label='Zoom out'
                    className='pointer-events-auto button-primary-filled w-[32px] h-[32px] !p-0'
                    onClick={() => onHighlightChange(previousPropertyId)}
                    type='button'>
                    <IconChevron className='rotate-90' />
                </button>

                <button
                    aria-label='Zoom in'
                    className='pointer-events-auto button-primary-filled w-[32px] h-[32px] !p-0'
                    onClick={() => onHighlightChange(nextPropertyId)}
                    type='button'>
                    <IconChevron className='-rotate-90' />
                </button>
            </div>
        </article>
    );
};

/**
 * @type {import('$types/components/partials/listings.d.ts').PropertyMapModal}
 */
export const PropertyMapModal = ({
    amenitiesFilter,
    areasFilter,
    bathroomsFilter,
    bedroomsFilter,
    defaultCentre,
    dialogFilterSelection,
    highlight,
    listing,
    mapBoundsFilter,
    newHomeFilter,
    officeFilter,
    onHighlightChange,
    onOpenChange,
    onSearch,
    onZoomChange,
    outdoorFilter,
    pageTitle,
    priceFilter,
    promotedFilterSelection,
    setSortBy,
    sizeFilter,
    sortBy,
    tenuresFilter,
    typesFilter,
    zoom,
}) => {
    const [mapOpen, setMapOpen] = useState(false);

    const highlightItems = listing.items?.filter((edge) => edge?.node?.isOffMarket === false);
    const highlightPropertyIndex = highlight ? highlightItems.findIndex((item) => item?.node?.id === highlight) : null;
    const highlightProperty = highlightPropertyIndex !== null ? highlightItems[highlightPropertyIndex]?.node : null;
    const nextPropertyId = highlightProperty ? highlightItems[highlightPropertyIndex < highlightItems.length - 1 ? highlightPropertyIndex + 1 : 0]?.node?.id : null;
    const previousPropertyId = highlightProperty ? highlightItems[highlightPropertyIndex > 0 ? highlightPropertyIndex - 1 : highlightItems.length - 1]?.node?.id : null;

    return (
        <>
            {/* Open */}
            <div className='sm:hidden sticky bottom-0 left-0 right-0 text-center pointer-events-none z-10 pb-16 md:pb-24'>
                <button
                    className='pointer-events-auto button-primary-filled type-style-eyebrow flex items-center !gap-8 h-[32px] sm:h-auto'
                    onClick={() => setMapOpen(true)}
                    type='button'>
                    <IconMap />
                    <span>Map</span>
                </button>
            </div>

            {/* Modal */}
            <Dialog
                className='theme-light sm:hidden'
                onOpenChange={(open) => {
                    setMapOpen(open);
                    if (typeof onOpenChange === 'function') {
                        onOpenChange(open);
                    }
                }}
                open={mapOpen}>
                <Title className='sr-only'>
                    Map of properties
                </Title>
                <Description className='sr-only'>
                    Find properties on a Google Map
                </Description>

                {/* Close */}
                {!highlight &&
                    <div className='absolute bottom-0 left-0 right-0 text-center pointer-events-none z-10 pb-16'>
                        <button
                            className='pointer-events-auto button-primary-filled type-style-eyebrow flex items-center !gap-8 h-[32px] sm:h-auto'
                            onClick={() => {
                                setMapOpen(false);
                                if (typeof onOpenChange === 'function') {
                                    onOpenChange(false);
                                }
                            }}
                            type='button'>
                            <IconList />
                            <span>List</span>
                        </button>
                    </div>
                }

                <div className='flex flex-col h-full'>
                    {/* Filters */}
                    <div className='bg-main-background-alt'>
                        <PropertyListingFilters
                            amenitiesFilter={amenitiesFilter}
                            areasFilter={areasFilter}
                            bathroomsFilter={bathroomsFilter}
                            bedroomsFilter={bedroomsFilter}
                            dialogFilterSelection={dialogFilterSelection}
                            mapBoundsFilter={mapBoundsFilter}
                            newHomeFilter={newHomeFilter}
                            officeFilter={officeFilter}
                            outdoorFilter={outdoorFilter}
                            pageTitle={pageTitle}
                            priceFilter={priceFilter}
                            promotedFilterSelection={promotedFilterSelection}
                            setSortBy={setSortBy}
                            sizeFilter={sizeFilter}
                            sortBy={sortBy}
                            tenuresFilter={tenuresFilter}
                            typesFilter={typesFilter} />
                    </div>

                    {/* Map */}
                    <div className='relative h-full flex-1'>
                        <PropertyMap
                            defaultBounds={mapBoundsFilter.value}
                            defaultCentre={defaultCentre}
                            highlight={highlight}
                            loading={listing.loading}
                            onHighlightChange={onHighlightChange}
                            onSearch={onSearch}
                            onZoomChange={onZoomChange}
                            properties={listing.items?.map((/** @type {{ node: object }} */ edge) => edge?.node)}
                            zoom={zoom} />
                    </div>

                    {highlightProperty &&
                        <PropertyMapModalHighlightProperty
                            {...highlightProperty}
                            nextPropertyId={nextPropertyId}
                            onHighlightChange={onHighlightChange}
                            previousPropertyId={previousPropertyId} />
                    }
                </div>
            </Dialog>
        </>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default PropertyMapModal;
